import React, { useMemo, useState } from 'react';
import { ArrowUpDown, Target, Ban, ChevronDown, ChevronUp } from 'lucide-react';
import type { Task } from './pmApi';
import { RiskLevel, EmptyState, fmtDate, cleanName } from './components';
import { Badge } from '../../../components/new-components/ui/badge';
import { Button } from '../../../components/new-components/ui/button';

/**
 * The work list.
 *
 * Rows rather than a table: a table forces every column to compete for width,
 * and at this density the useful signal is the item, its state, and why it
 * matters — not a grid of cells. Rows are selectable, keyboard-reachable, and
 * grouped by phase when a single department is in view, so the plan's own
 * structure stays legible instead of flattening into 257 undifferentiated lines.
 */

type SortKey = 'risk' | 'due' | 'name' | 'progress';

const STATUS_TONE: Record<string, string> = {
  done: 'text-emerald-600 dark:text-emerald-400',
  in_progress: 'text-blue-600 dark:text-blue-400',
  blocked: 'text-red-600 dark:text-red-400',
  not_started: 'text-muted-foreground',
};

function TaskRow({ t, selected, onSelect }: { t: Task; selected: boolean; onSelect: () => void }) {
  const overdue = t.risk.reasons.some((r) => r.code === 'overdue');
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected}
      className={`flex w-full items-center gap-3 border-l-2 px-3 py-2.5 text-left transition-colors ${
        selected
          ? 'border-l-primary bg-primary/5'
          : 'border-l-transparent hover:bg-muted/50'
      }`}
    >
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-sm font-medium text-[#242424] dark:text-foreground">{cleanName(t.name)}</span>
          {t.isMilestone ? <Target className="h-3 w-3 shrink-0 text-muted-foreground" aria-label="Milestone" /> : null}
          {t.blocked ? <Ban className="h-3 w-3 shrink-0 text-red-500" aria-label="Blocked" /> : null}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <span className={STATUS_TONE[t.statusNormalized]}>{t.status}</span>
          <span>{t.owner || 'Unassigned'}</span>
          <span className={`tabular-nums ${overdue ? 'text-orange-600 dark:text-orange-400' : ''}`}>
            {fmtDate(t.dueDate)}
          </span>
          <span className="tabular-nums">{t.percentComplete}%</span>
          {!t.nextAction ? <span className="text-orange-600 dark:text-orange-400">no next action</span> : null}
        </span>
      </span>
      <RiskLevel level={t.risk.level} />
    </button>
  );
}

function PhaseGroup({
  phase, tasks, selectedId, onSelect,
}: {
  phase: string;
  tasks: Task[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const overdue = tasks.filter((t) => t.risk.reasons.some((r) => r.code === 'overdue')).length;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 bg-muted/40 px-3 py-1.5 text-left"
        aria-expanded={open}
      >
        {open ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground/80">{cleanName(phase)}</span>
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {overdue ? <span className="text-orange-600 dark:text-orange-400">{overdue} late · </span> : null}
          {tasks.length}
        </span>
      </button>
      {open ? (
        <div className="divide-y divide-border">
          {tasks.map((t) => (
            <TaskRow key={t.id} t={t} selected={selectedId === t.id} onSelect={() => onSelect(t.id)} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function TaskList({
  tasks, selectedId, onSelect, groupByPhase,
}: {
  tasks: Task[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  groupByPhase: boolean;
}) {
  const [sort, setSort] = useState<SortKey>('risk');

  const sorted = useMemo(() => {
    const copy = [...tasks];
    copy.sort((a, b) => {
      if (sort === 'risk') return b.risk.score - a.risk.score;
      if (sort === 'progress') return a.percentComplete - b.percentComplete;
      if (sort === 'name') return cleanName(a.name).localeCompare(cleanName(b.name));
      // due: undated last
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate.localeCompare(b.dueDate);
    });
    return copy;
  }, [tasks, sort]);

  const grouped = useMemo(() => {
    if (!groupByPhase) return null;
    // Plain object + explicit order array rather than spreading a Map iterator:
    // this app targets ES5, where iterator spread is a compile error.
    const buckets: Record<string, Task[]> = {};
    const order: string[] = [];
    sorted.forEach((t) => {
      const key = t.phase || 'Unphased';
      if (!buckets[key]) { buckets[key] = []; order.push(key); }
      buckets[key].push(t);
    });
    return order.map((key) => [key, buckets[key]] as [string, Task[]]);
  }, [sorted, groupByPhase]);

  const SORTS: Array<{ key: SortKey; label: string }> = [
    { key: 'risk', label: 'Risk' },
    { key: 'due', label: 'Due date' },
    { key: 'progress', label: 'Least progress' },
    { key: 'name', label: 'Name' },
  ];

  if (!tasks.length) {
    return <EmptyState title="Nothing matches">Clear a filter, or pick a different department.</EmptyState>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Sort</span>
        {SORTS.map((s) => (
          <Button
            key={s.key}
            variant={sort === s.key ? 'secondary' : 'ghost'}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setSort(s.key)}
          >
            {s.label}
          </Button>
        ))}
        <Badge variant="outline" className="ml-auto text-[10px] font-normal tabular-nums">
          {tasks.length} item{tasks.length === 1 ? '' : 's'}
        </Badge>
      </div>

      {grouped ? (
        <div className="divide-y divide-border">
          {grouped.map(([phase, list]) => (
            <PhaseGroup key={phase} phase={phase} tasks={list} selectedId={selectedId} onSelect={onSelect} />
          ))}
        </div>
      ) : (
        <div className="divide-y divide-border">
          {sorted.map((t) => (
            <TaskRow key={t.id} t={t} selected={selectedId === t.id} onSelect={() => onSelect(t.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
